import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Waves, Anchor, Calendar, Loader2, Trash2, RefreshCw, BookOpenCheck, ChevronRight } from 'lucide-react';
import PwaInstallBanner from '../../components/PwaInstallBanner';
import { viewerStore } from './viewerStore';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlannerViewer = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState(viewerStore.list());
  const [refreshing, setRefreshing] = useState(false);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const updated = await Promise.all(items.map(async (s) => {
        try {
          const r = await axios.get(`${API_URL}/planner/share/${s.token}`);
          return { ...s, trip_name: r.data.trip_name, date: r.data.date, valid: true };
        } catch (e) {
          return { ...s, valid: false };
        }
      }));
      setItems(updated);
      // Re-persist freshened data (drop invalid ones).
      const stillValid = updated.filter((s) => s.valid !== false);
      stillValid.forEach((s) => viewerStore.add({ token: s.token, trip_name: s.trip_name, date: s.date }));
      updated.filter((s) => s.valid === false).forEach((s) => viewerStore.remove(s.token));
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (items.length === 0) {
      navigate('/planner', { replace: true });
      return;
    }
    refresh();
    // eslint-disable-next-line
  }, []);

  const remove = (token) => {
    viewerStore.remove(token);
    setItems(viewerStore.list());
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-32" data-testid="planner-viewer">
      <div className="bg-gradient-to-br from-[#0A2342] to-[#1a3a5c] text-white px-5 pt-10 pb-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Waves className="w-7 h-7 text-[#FF6B35]" />
              <h1 className="text-xl md:text-2xl font-bold">My Dive Schedules</h1>
            </div>
            <button
              onClick={refresh}
              disabled={refreshing}
              className="text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50"
              data-testid="viewer-refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
          <p className="text-xs opacity-80 mt-2">Schedules shared with you appear here. Open a share link and it'll be saved automatically.</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 mt-4 space-y-3">
        {items.length > 0 && (
          <Link
            to="/planner/my-dives"
            className="bg-gradient-to-r from-[#FF6B35] to-[#ff8a5b] text-white rounded-2xl p-4 flex items-center gap-3 shadow-sm hover:shadow-md transition"
            data-testid="viewer-my-dives-cta"
          >
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
              <BookOpenCheck className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold">My Logged Dives</div>
              <div className="text-xs opacity-90">See every dive your instructor logged for you — copy them straight to your PADI / SSI / NAUI / ISC log book.</div>
            </div>
            <ChevronRight className="w-5 h-5 opacity-90" />
          </Link>
        )}
        {items.length === 0 ? (
          <div className="bg-white rounded-xl border p-10 text-center">
            <Anchor className="w-10 h-10 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 text-sm">No schedules saved yet. Open a share link from your dive operator to add one.</p>
          </div>
        ) : (
          items.map((s) => (
            <div key={s.token} className="bg-white rounded-2xl border p-4 flex items-center gap-3" data-testid={`viewer-item-${s.token}`}>
              <Link to={`/planner/share/${s.token}`} className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Anchor className="w-4 h-4 text-[#FF6B35] shrink-0" />
                  <span className="font-semibold text-[#0A2342] truncate">{s.trip_name || 'Dive schedule'}</span>
                  {s.valid === false && <span className="text-[10px] text-red-600 bg-red-50 px-1.5 py-0.5 rounded">expired</span>}
                </div>
                <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                  <Calendar className="w-3 h-3" /> {s.date || '—'}
                </div>
              </Link>
              <button
                onClick={() => remove(s.token)}
                className="text-gray-300 hover:text-red-500 p-1"
                aria-label="Remove"
                data-testid={`viewer-remove-${s.token}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))
        )}
        {refreshing && items.length > 0 && (
          <div className="text-xs text-gray-400 flex items-center gap-1 justify-center pt-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Updating schedules…
          </div>
        )}
      </div>
      <PwaInstallBanner />
    </div>
  );
};

export default PlannerViewer;
